import { useZero } from "@rocicorp/zero/react";
import { useState } from "react";
import { sharedMutators } from "../../../../shared/mutators";
import { useNow } from "../../../hooks/useNow";
import { formatDate } from "../../../lib/formatTimestamp";
import { ConfirmModal } from "../../ConfirmModal";
import { StatusBadge } from "../../StatusBadge";
import { Text } from "../../Text";
import { daysUntil, SCIM_TOKEN_WARNING_DAYS } from "./providerStatus";
import { FieldLink } from "./shared";

export type ScimToken = {
  providerId: string;
  expiresAt?: number | null;
  onRevoked: () => void;
};

export function ScimTokenBadge({ expiresAt }: { expiresAt?: number | null }) {
  const now = useNow(60 * 60 * 1000);
  if (expiresAt == null) return null;
  const days = daysUntil(expiresAt, now);
  if (days <= 0) return <StatusBadge color="red">Expired</StatusBadge>;
  if (days <= SCIM_TOKEN_WARNING_DAYS)
    return <StatusBadge color="orange">Expiring</StatusBadge>;
  return null;
}

export function ScimTokenStatus({
  providerId,
  expiresAt,
  onRevoked,
}: ScimToken) {
  const z = useZero();
  const now = useNow(60 * 60 * 1000);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const days = expiresAt != null ? daysUntil(expiresAt, now) : null;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      {expiresAt != null && (
        <>
          <Text variant="text-small" color="var(--text-secondary)">
            {days! <= 0 ? "Token expired" : "Token expires"}{" "}
            {formatDate(expiresAt)}
          </Text>
          <Text variant="text-small" color="var(--text-tertiary)">
            ·
          </Text>
        </>
      )}
      <FieldLink onPress={() => setConfirmRevoke(true)}>
        <Text variant="text-small">Revoke</Text>
      </FieldLink>
      <ConfirmModal
        open={confirmRevoke}
        onClose={() => setConfirmRevoke(false)}
        onConfirm={() => {
          z.mutate(
            sharedMutators.revokeScimToken({ ssoProviderId: providerId })
          );
          setConfirmRevoke(false);
          onRevoked();
        }}
        title="Revoke SCIM token"
        description="The current token stops working right away, so your IdP can’t sync. To turn SCIM back on, choose it as the sync mode again and you’ll get a new token."
        confirmLabel="Revoke"
        destructive
      />
    </div>
  );
}
